'use client';

import React from 'react';
import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useAnalytics } from '@/hooks/useAnalytics';

// Define button variants using class-variance-authority
const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background',
  {
    variants: {
      variant: {
        default: 'bg-gray-800 text-white hover:bg-gray-700',
        destructive: 'bg-red-600 text-white hover:bg-red-700',
        outline: 'border border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white',
        secondary: 'bg-gray-700 text-white hover:bg-gray-600',
        ghost: 'hover:bg-gray-800 hover:text-gray-300 text-gray-400',
        link: 'underline-offset-4 hover:underline text-blue-400',
        gradient: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all',
        athletic: 'bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 text-white hover:from-blue-700 hover:via-indigo-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all border border-blue-400/20',
        neon: 'bg-[#00FF66] text-[#050505] hover:bg-[#00CC55] shadow-[0_0_20px_rgba(0,255,102,0.5)] hover:shadow-[0_0_30px_rgba(0,255,102,0.7)] font-bold transition-all duration-300 border-none',
        minimal: 'bg-transparent text-white border-2 border-white/10 hover:border-white/30 hover:bg-white/5 transition-all duration-300',
        futuristic: 'bg-[#050505] text-white border border-white/20 hover:border-white/40 backdrop-blur-sm hover:bg-white/5 transition-all duration-300',
        tesla: 'bg-[#050505] text-white hover:bg-[#111111] border border-[#222222] hover:border-[#333333] transition-all duration-300 font-medium',
        nike: 'bg-black text-white hover:bg-[#111111] font-bold tracking-wide transition-all duration-300 transform hover:-translate-y-0.5',
        spacex: 'bg-black text-white hover:bg-[#111111] uppercase tracking-widest text-xs font-bold transition-all duration-300',
      },
      size: {
        default: 'h-10 py-2 px-4',
        sm: 'h-9 px-3 rounded-md',
        lg: 'h-11 px-8 rounded-md',
        xl: 'h-14 px-10 rounded-lg text-base',
        '2xl': 'h-16 px-12 rounded-lg text-lg font-semibold',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  href?: string;
  target?: string;
  rel?: string;
  trackingName?: string;
  trackingLocation?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    href,
    target,
    rel, 
    trackingName, 
    trackingLocation, 
    children, 
    isLoading, 
    leftIcon, 
    rightIcon,
    ...props 
  }, ref) => {
    const { trackClick } = useAnalytics();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (trackingName) {
        trackClick(trackingName, trackingLocation);
      }
      
      if (props.onClick) {
        props.onClick(e as React.MouseEvent<HTMLButtonElement>);
      }
    };

    const content = (
      <>
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="ml-2">{rightIcon}</span>}
      </>
    );

    if (href) {
      return (
        <Link
          href={href}
          target={target}
          rel={rel}
          className={cn(buttonVariants({ variant, size, className }))}
          onClick={handleClick}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants }; 